import React, { useState, useEffect } from 'react';
import API, { SESSION_NOTICE_KEY } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { setAuth, getToken, getUser } from '../utils/authUtil';
import Icon from '../components/ui/Icon';
import ZapLogo from '../components/ui/ZapLogo';

const NOTICES = {
  session_expired: 'Your session has ended. Please sign in again to continue.',
};

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Why the student is back here (e.g. their session expired) — read once, then cleared.
  const [notice] = useState(() => {
    try { const k = sessionStorage.getItem(SESSION_NOTICE_KEY); sessionStorage.removeItem(SESSION_NOTICE_KEY); return NOTICES[k] || ''; } catch { return ''; }
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    const user = getUser();
    if (token && user) {
      const role = user.role;
      if (role === 'super_admin') {
        navigate('/super-admin');
      } else if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'student') {
        navigate('/student/dashboard');
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/login', {
        email: form.email.toLowerCase().trim(),
        password: form.password
      });

      const user = res.data.user;
      const role = user.role;

      setAuth(res.data.token, user);

      // Single-domain SPA navigation — never change the domain, only React Router.
      if (role === 'super_admin') {
        navigate('/super-admin');
      } else if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'student') {
        navigate('/student/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err.response?.data?.message || (err.response ? 'Invalid email or password' : 'You appear to be offline. Check your connection and try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-hero" aria-hidden="true" />

      <main className="login-card">
        <div className="login-header">
          <ZapLogo height={64} />
          <p>Build your placement-ready aptitude, one challenge at a time.</p>
        </div>

        {notice && <div className="login-notice" role="status"><Icon name="info" size={18} /> {notice}</div>}
        {error && <div className="login-error" role="alert"><Icon name="warning" size={18} /> {error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              name="email"
              placeholder="you@college.edu"
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck="false"
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <div className="login-password-wrap">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter password"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                <Icon name={showPassword ? 'eye-off' : 'eye'} size={20} />
              </button>
            </div>
          </div>
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Signing in…' : 'Enter Zaptude'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default Login;
